import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { FindFreeRoomModal } from "@nodus/booking";
import { meetingRoomsApi } from "@/lib/api/client";
import { warnIfPending } from "@/lib/booking-approval-toast";

export interface FindFreeRoomDialogProps {
  opened: boolean;
  onClose: () => void;
  /** Called after a room was booked — or, in practice mode, after the member
   *  pressed a booking button. */
  onBooked?: () => void;
  /**
   * Practice mode (a guide teaching how to book): the rooms and routes are
   * real, but a booking button books nothing — no API call. The dialog
   * closes and `onBooked` is called; the caller says it was practice.
   */
  practice?: boolean;
}

/**
 * "Find a free meeting room" wired to the member portal's API — the dashboard
 * card and the guides' `meeting.find_room` block both open it.
 */
export function FindFreeRoomDialog({ opened, onClose, onBooked, practice = false }: FindFreeRoomDialogProps) {
  // A practice run starts a new modal: the shared modal would otherwise go on
  // to its own "Booked!" screen, which practice must never show.
  const [run, setRun] = useState(0);
  const fetchFreeRooms = useCallback(async (durationMinutes: number) => {
    const res = await meetingRoomsApi.findFreeRooms(durationMinutes);
    return res.data;
  }, []);
  const bookFreeRoom = useCallback(
    async (roomId: number, minutes: number) => {
      if (practice) {
        setRun((r) => r + 1);
        onClose();
        onBooked?.();
        // Never settles: the modal that asked is unmounted (new `key`), so it
        // never reaches its confirmation, and nothing is left waiting on it.
        return new Promise<{ end: string }>(() => {});
      }
      const start = new Date();
      const end = new Date(start.getTime() + minutes * 60_000);
      const res = await meetingRoomsApi.bookRoom(roomId, {
        title: `Booking (${minutes} min)`,
        start: start.toISOString(),
        end: end.toISOString(),
        description: "Booked via Find a free meeting room",
        source: "member_find_free",
      } as Parameters<typeof meetingRoomsApi.bookRoom>[1]);
      warnIfPending(res.data);
      onBooked?.();
      return { end: res.data.end };
    },
    [onBooked, onClose, practice],
  );
  const fetchWayfinding = useCallback(async (roomId: number) => {
    const res = await meetingRoomsApi.wayfind(roomId);
    return res.data;
  }, []);

  return (
    <FindFreeRoomModal
      key={run}
      opened={opened}
      onClose={onClose}
      title={practice ? "Find a free meeting room (practice)" : undefined}
      fetchFreeRooms={fetchFreeRooms}
      bookRoom={bookFreeRoom}
      fetchWayfinding={fetchWayfinding}
      onError={(err, context) => Sentry.captureException(err, { tags: { feature: "fafr", context } })}
    />
  );
}
